/**
 * UserListTable Component
 *
 * Display paginated user list with actions
 * Feature: 045-admin-user-tool
 */

import { Link } from 'react-router-dom';
import type { AdminUser } from '../../graphql/hooks/useAdminUsersGraphQL';
import { adminUserDetailUrl } from '../../utils/urls';

// Re-export AdminUser as User for compatibility
type User = AdminUser;
import { IdLink } from '../IdLink';

interface UserListTableProps {
  users: User[];
  loading?: boolean;
  page: number;
  limit: number;
  total: number;
  onPageChange: (page: number) => void;
}

export function UserListTable({
  users,
  loading,
  page,
  limit,
  total,
  onPageChange
}: UserListTableProps) {
  const totalPages = Math.ceil(total / limit);
  const startIndex = (page - 1) * limit + 1;
  const endIndex = Math.min(page * limit, total);

  if (loading) {
    return (
      <div className="bg-surface-1 rounded-lg border border-border overflow-hidden">
        <div className="animate-pulse p-6">
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-surface-2 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="bg-surface-1 rounded-lg border border-border p-8 text-center">
        <p className="text-muted">No users found matching your criteria.</p>
      </div>
    );
  }

  return (
    <div className="bg-surface-1 rounded-lg border border-border overflow-hidden">
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-surface-2 border-b border-border">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">
                Created
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-muted uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {users.map((user) => (
              <UserRow key={user.globalId} user={user} />
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="bg-surface-2 px-6 py-3 border-t border-border flex items-center justify-between">
        <div className="text-sm text-muted">
          Showing {startIndex} to {endIndex} of {total} users
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => onPageChange(page - 1)}
            disabled={page === 1}
            className="px-3 py-1 text-sm border border-border rounded-md hover:bg-surface-3 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Previous
          </button>
          <div className="flex items-center gap-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const pageNum = i + 1;
              return (
                <button
                  key={pageNum}
                  onClick={() => onPageChange(pageNum)}
                  className={`px-3 py-1 text-sm rounded-md transition-colors ${
                    page === pageNum
                      ? 'bg-primary text-primary-foreground'
                      : 'border border-border hover:bg-surface-3'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            {totalPages > 5 && (
              <>
                <span className="px-2 text-muted">...</span>
                <button
                  onClick={() => onPageChange(totalPages)}
                  className={`px-3 py-1 text-sm rounded-md transition-colors ${
                    page === totalPages
                      ? 'bg-primary text-primary-foreground'
                      : 'border border-border hover:bg-surface-3'
                  }`}
                >
                  {totalPages}
                </button>
              </>
            )}
          </div>
          <button
            onClick={() => onPageChange(page + 1)}
            disabled={page === totalPages}
            className="px-3 py-1 text-sm border border-border rounded-md hover:bg-surface-3 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

interface UserRowProps {
  user: User;
}

function UserRow({ user }: UserRowProps) {
  const createdDate = new Date(user.createdAt).toLocaleDateString();

  return (
    <tr className="hover:bg-surface-2 transition-colors">
      <td className="px-6 py-4 whitespace-nowrap">
        <div>
          <div className="text-sm font-medium text-foreground">{user.username}</div>
          <div className="text-xs">
            <IdLink globalId={user.globalId} typeName="User" className="text-xs text-muted" />
          </div>
        </div>
      </td>
      <td className="px-6 py-4">
        <div className="text-sm text-foreground">{user.email}</div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex gap-2">
          {user.emailVerified ? (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-500">
              Verified
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-500">
              Unverified
            </span>
          )}
          {user.isTestUser && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-500/20 text-purple-500">
              Test
            </span>
          )}
          {user.isAdmin && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-500">
              Admin
            </span>
          )}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-muted">{createdDate}</div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <Link
          to={adminUserDetailUrl(user.globalId)}
          className="text-primary hover:text-primary/80 transition-colors"
        >
          View Details
        </Link>
      </td>
    </tr>
  );
}
